import React from 'react';
import MuiAccordion, { accordionClasses } from '@mui/material/Accordion';
import AccordionDetails, { accordionDetailsClasses } from '@mui/material/AccordionDetails';
import AccordionSummary, { accordionSummaryClasses } from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import { styled, lighten } from '@mui/material/styles';
import { Chip } from '../../Chip';
import { Typography } from '../../Typography';
import { AccordionProps } from '../types';
const ExpandMoreIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      d="M5 7.5L10 12.5L15 7.5"
      stroke="#667085"
      strokeWidth="1.66667"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const StyledAccordion = styled(MuiAccordion, {
  shouldForwardProp: (prop) =>
    prop !== 'expandIconPosition' && prop !== 'hasBorder' && prop !== 'chipPosition' && prop !== 'styleMode',
})<Pick<AccordionProps, 'expandIconPosition' | 'hasBorder' | 'chipPosition' | 'styleMode'>>(
  ({ theme, expandIconPosition, hasBorder, chipPosition, styleMode }) => ({
    [`&.${accordionClasses.root}`]: {
      marginBottom: '6px',
      borderRadius: '12px',
      backgroundColor: '#FFF',
      boxShadow: 'none',
      border: hasBorder ? `1px solid ${theme.custom.colours?.grey300}` : 'none',
      '&:before': {
        // Remove the divider
        display: 'none',
      },
      [`& .${accordionSummaryClasses.root}`]: {
        padding: '8px 12px',
        gap: '12px',
        flexDirection: expandIconPosition === 'left' ? 'row-reverse' : undefined,
        [`& .${accordionSummaryClasses.expandIconWrapper}`]: {
          // When the icon is on the left, we want it to point down when the accordion is expanded and for it to point right when it is collapsed
          transform: expandIconPosition === 'left' ? 'rotate(-90deg)' : undefined,
          [`&.${accordionSummaryClasses.expanded}`]: {
            // When the icon is on the left, we want it to point down when the accordion is expanded and for it to point right when it is collapsed
            transform: expandIconPosition === 'left' ? 'rotate(0deg)' : undefined,
          },
        },
        borderRadius: '12px',
        // Hover state
        '&:hover': {
          backgroundColor: theme.custom.colours?.grey50,
          transition: 'background-color 0.2s ease-in-out',
        },
        // Focus state
        '&.Mui-focusVisible': {
          backgroundColor: '#FFF',
          outline: `1px solid ${lighten(theme.palette.primary.main, 0.5)}`,
          boxShadow: `0px 1px 2px 0px rgba(16, 24, 40, 0.05), 0px 0px 0px 4px ${lighten(
            theme.palette.primary.main,
            0.9,
          )}`,
        },
        [`& .${accordionSummaryClasses.content}`]: {
          margin: 0,
          flexDirection: 'row',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: '12px',
          // Accordion title
          '& .MuiTypography-root': {
            fontSize: styleMode === 'compact' ? '14px' : '16px',
            fontWeight: styleMode === 'compact' ? 600 : 400,
            lineHeight: styleMode === 'compact' ? '20px' : '24px',
            color: styleMode === 'compact' ? '#414651' : theme.custom.colours?.grey700,
          },
          // Count chip
          '& .MuiChip-root': {
            // We want the chip to be aligned to the right of the title
            marginLeft: chipPosition === 'start' ? '8px' : 'auto',
            marginRight: '8px',
          },
        },
      },
      [`& .${accordionDetailsClasses.root}`]: {
        marginTop: '4px',
        padding: '8px 12px 16px 12px',
      },
    },
  }),
);
export const Accordion: React.FC<AccordionProps> = ({
  idPrefix,
  title,
  content,
  expandIconPosition = 'right',
  startAdornment,
  chipCount,
  chipLabel,
  chipPosition = 'end',
  chipColor,
  headerEndslots,
  hasBorder = false,
  titleComponent,
  styleMode = 'default',
  headerEndSlotsFullWidth = false,
  ...props
}: AccordionProps): JSX.Element => {
  return (
    <StyledAccordion
      data-testid={`${idPrefix}-accordion`}
      disableGutters
      expandIconPosition={expandIconPosition}
      hasBorder={hasBorder}
      chipPosition={chipPosition}
      styleMode={styleMode}
      {...props}
    >
      {titleComponent ? (
        titleComponent
      ) : (
        <AccordionSummary
          component="div"
          expandIcon={ExpandMoreIcon}
          aria-controls={`${idPrefix}-content`}
          id={`${idPrefix}-header`}
        >
          {startAdornment}
          <Typography>{title}</Typography>
          {typeof chipCount === 'number' ? (
            <Chip data-testid="chip-count" size="small" label={chipCount} color={chipColor} />
          ) : chipLabel ? (
            <Chip data-testid="chip-label" size="small" label={chipLabel} color={chipColor} />
          ) : null}
          {headerEndslots ? (
            <Box
              // When the content within the accordion is focused/clicked, we don't want the accordion to show a focus state
              onFocus={(e) => {
                e.stopPropagation();
              }}
              sx={{
                marginLeft: headerEndSlotsFullWidth ? undefined : 'auto',
                width: headerEndSlotsFullWidth ? '100%' : undefined,
                display: 'flex',
                alignItems: {
                  xs: 'flex-end',
                  md: 'center',
                },
                gap: {
                  xs: 4,
                  md: 2,
                },
                flexDirection: {
                  xs: 'column',
                  md: 'row',
                },
                flexGrow: {
                  xs: 1,
                  md: 0,
                },
              }}
            >
              {headerEndslots}
            </Box>
          ) : null}
        </AccordionSummary>
      )}
      <AccordionDetails data-testid={`${idPrefix}-accordion-details`}>{content}</AccordionDetails>
    </StyledAccordion>
  );
};
